import React from 'react';
import { useTranslation } from 'react-i18next';
import { AudioTrack } from '../types';
import { useLocale } from '../hooks/useLocale';
import { getLocalizedValue } from '../utils/i18n';

type SubscriptionStatus =
  | 'free'
  | 'trial'
  | 'active'
  | 'canceled'
  | 'past_due'
  | 'expired';

interface AudioTrackCardProps {
  track: AudioTrack;
  onSelect: (track: AudioTrack) => void;
  isSelected: boolean;
  subscriptionStatus?: SubscriptionStatus;
  trialEndDate?: string;
}

const AudioTrackCard: React.FC<AudioTrackCardProps> = ({
  track,
  onSelect,
  isSelected,
  subscriptionStatus,
  trialEndDate,
}) => {
  const { t } = useTranslation('tracks');
  const { currentLocale } = useLocale();
  const handleClick = () => onSelect(track);

  // CDN URL from environment variable
  const cdnUrl = process.env.REACT_APP_CDN_URL || '';

  // Format duration from seconds to minutes
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    return `${mins} min`;
  };

  // Get full thumbnail URL with CDN prefix
  const getThumbnailUrl = (thumbnail?: string): string | undefined => {
    if (!thumbnail) return undefined;
    // If thumbnail already starts with http:// or https://, use it as-is
    if (thumbnail.startsWith('http://') || thumbnail.startsWith('https://')) {
      return thumbnail;
    }
    // Otherwise, prefix with CDN URL
    const separator = thumbnail.startsWith('/') ? '' : '/';
    return `${cdnUrl}${separator}${thumbnail}`;
  };

  const thumbnailUrl = getThumbnailUrl(track.thumbnail);

  return (
    <div
      className={`track-card-vertical ${isSelected ? 'playing' : ''}`}
      onClick={handleClick}
    >
      <div className='track-card-header'>
        {thumbnailUrl && (
          <div className='track-card-thumbnail'>
            <img
              src={thumbnailUrl}
              alt={getLocalizedValue(track.name, currentLocale)}
              className='track-thumbnail-image'
            />
            <div className='track-thumbnail-overlay'></div>
          </div>
        )}

        <div className='track-card-content'>
          <div
            style={{
              paddingRight: '8px',
              flex: 1,
            }}
          >
            <h3 className='track-card-title-large'>
              {getLocalizedValue(track.name, currentLocale)}
            </h3>
            <p className='track-card-description-large'>
              {getLocalizedValue(track.description, currentLocale)}
            </p>
            {track.tags && track.tags.length > 0 && (
              <div className='track-tags'>
                {track.tags.map((tag) => (
                  <span key={tag} className='track-tag-chip'>
                    {t(`tags.${tag}`, tag)}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className='track-card-metadata'>
            <div className='track-play-button'>
              <svg
                width='18'
                height='18'
                viewBox='0 0 24 24'
                fill='currentColor'
              >
                <polygon points='5,3 19,12 5,21' />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioTrackCard;
